/**
 * =============================================================================
 * Hook: useCreateManualAffiliateReferral
 * Versão: 1.0.0
 *
 * Descrição:
 *   Mutation para o afiliado cadastrar uma indicação manualmente, a partir do
 *   painel `/afiliado`. Reaproveita a RPC pública `capture_affiliate_lead`,
 *   passando o `slug` do link pessoal do afiliado para a campanha escolhida e
 *   marcando `lead_source = 'manual_affiliate'` (para diferenciar nos KPIs).
 *
 *   Decisão arquitetural: NÃO criamos uma RPC nova. A `capture_affiliate_lead`
 *   já valida ownership (slug → afiliado), membership ativa, link ativo,
 *   tenant e dispara o trigger AFTER INSERT que cria contato/deal no CRM e
 *   notificações. Reuso integral evita fragmentação no backend.
 * =============================================================================
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

const log = createLogger('useCreateManualAffiliateReferral');

/** Parâmetros aceitos pelo hook (espelham a RPC, com slug obrigatório). */
export interface CreateManualReferralInput {
  /** Slug do link pessoal do afiliado para a campanha escolhida. */
  slug: string;
  lead_name?: string | null;
  lead_email?: string | null;
  lead_phone?: string | null;
  lead_message?: string | null;
}

/**
 * Mapeia mensagens conhecidas da RPC para feedback amigável em PT-BR.
 * Mantém a mensagem original quando não casar com nenhum padrão.
 */
function friendlyError(message: string): string {
  const m = (message || '').toLowerCase();
  if (m.includes('link não encontrado') || m.includes('link nao encontrado')) {
    return 'Campanha inválida ou removida. Recarregue a página.';
  }
  if (m.includes('link inativo')) {
    return 'Esta campanha está inativa no momento.';
  }
  if (m.includes('cross-tenant')) {
    return 'Acesso negado para esta campanha.';
  }
  if (m.includes('informe ao menos')) {
    return 'Informe ao menos nome, e-mail ou telefone.';
  }
  return message || 'Falha ao criar indicação.';
}

export function useCreateManualAffiliateReferral() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateManualReferralInput) => {
      const { slug, lead_name, lead_email, lead_phone, lead_message } = input;

      if (!slug) {
        throw new Error('Selecione uma campanha.');
      }

      log.debug('Criando indicação manual', { slug, has_email: !!lead_email, has_phone: !!lead_phone });

      const { data, error } = await supabase.rpc('capture_affiliate_lead', {
        p_slug: slug,
        p_lead_name: lead_name ?? null,
        p_lead_email: lead_email ?? null,
        p_lead_phone: lead_phone ?? null,
        p_lead_message: lead_message ?? null,
        p_cookie_id: null,
        // Marca de origem usada para distinguir das capturas via landing/redirect.
        p_lead_source: 'manual_affiliate',
        p_tenant_hint: null,
      });

      if (error) {
        log.error('Erro ao chamar capture_affiliate_lead', error);
        throw new Error(friendlyError(error.message));
      }

      return data as string; // referral_id (uuid)
    },
    onSuccess: () => {
      // Invalida tudo que reflete a nova indicação na home/listas/KPIs.
      qc.invalidateQueries({ queryKey: ['affiliate-referrals'] });
      qc.invalidateQueries({ queryKey: ['affiliate-dashboard-kpis'] });
      qc.invalidateQueries({ queryKey: ['affiliate-home-overview'] });
      qc.invalidateQueries({ queryKey: ['affiliate-monthly-progress'] });
      qc.invalidateQueries({ queryKey: ['affiliate-monthly-evolution'] });
      toast.success('Indicação criada com sucesso!');
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });
}
